import React, { useState, useEffect } from 'react';
import { FaPlay, FaExclamationTriangle } from 'react-icons/fa';

const VideoThumbnail = ({ 
  src, 
  style = {}, 
  className = '', 
  onClick,
  showPlayButton = true,
  fallbackGradient = 'var(--light-brown), var(--warm-beige)'
}) => {
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  // const videoRef = useRef(null);
  // const canvasRef = useRef(null);

  useEffect(() => {
    if (!src) {
      setHasError(true);
      setIsLoading(false);
      return;
    }

    const generateThumbnail = () => {
      const video = document.createElement('video');
      video.crossOrigin = 'anonymous';
      video.muted = true;
      video.preload = 'metadata';
      
      const handleLoadedData = () => {
        try {
          // Aller à 2 secondes ou 10% de la vidéo
          const seekTime = Math.min(2, video.duration * 0.1);
          video.currentTime = seekTime;
        } catch (error) {
          console.error('Erreur lors du seek:', error);
          setHasError(true);
          setIsLoading(false);
        }
      };

      const handleSeeked = () => {
        try {
          // Créer un canvas pour capturer la frame
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          
          // Dessiner la frame actuelle
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          
          // Convertir en URL de données
          const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
          setThumbnailUrl(dataUrl);
          setIsLoading(false);
          
        } catch (error) {
          console.error('Erreur lors de la génération de miniature:', error);
          setHasError(true);
          setIsLoading(false);
        }
      };

      const handleError = (error) => {
        console.error('Erreur vidéo pour miniature:', error);
        setHasError(true);
        setIsLoading(false);
      };

      video.addEventListener('loadeddata', handleLoadedData);
      video.addEventListener('seeked', handleSeeked);
      video.addEventListener('error', handleError);
      
      video.src = src;
    };

    generateThumbnail();
  }, [src]);

  const handleClick = (e) => {
    if (onClick) {
      onClick(e);
    }
  };

  // Rendu en cas d'erreur
  if (hasError) {
    return (
      <div
        style={{
          ...style,
          background: `linear-gradient(135deg, ${fallbackGradient})`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: '10px',
          color: 'white',
          cursor: onClick ? 'pointer' : 'default'
        }}
        className={className}
        onClick={handleClick}
      >
        <FaExclamationTriangle size={24} />
        <span style={{ fontSize: '0.9em', textAlign: 'center' }}>
          Miniature indisponible
        </span>
        {showPlayButton && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            padding: '15px',
            borderRadius: '50%',
            cursor: 'pointer',
            zIndex: 2
          }}>
            <FaPlay size={20} />
          </div>
        )}
      </div>
    );
  }

  // Rendu pendant le chargement
  if (isLoading) {
    return (
      <div
        style={{
          ...style,
          background: `linear-gradient(135deg, ${fallbackGradient})`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          cursor: onClick ? 'pointer' : 'default'
        }}
        className={className}
        onClick={handleClick}
      >
        <div style={{
          width: '30px',
          height: '30px',
          border: '3px solid rgba(255,255,255,0.3)',
          borderTop: '3px solid white',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <style jsx>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Rendu avec miniature générée
  return (
    <div
      style={{
        ...style,
        position: 'relative',
        cursor: onClick ? 'pointer' : 'default',
        overflow: 'hidden'
      }}
      className={className}
      onClick={handleClick}
    >
      <img
        src={thumbnailUrl}
        alt="Miniature vidéo"
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          borderRadius: 'inherit'
        }}
      />
      
      {/* Bouton play overlay */}
      {showPlayButton && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          color: 'white',
          padding: '15px',
          borderRadius: '50%',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          zIndex: 2
        }}
        onMouseEnter={(e) => {
          e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.9)';
          e.target.style.transform = 'translate(-50%, -50%) scale(1.1)';
        }}
        onMouseLeave={(e) => {
          e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
          e.target.style.transform = 'translate(-50%, -50%) scale(1)';
        }}
        >
          <FaPlay size={20} />
        </div>
      )}
    </div>
  );
};

export default VideoThumbnail;